import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  IoCashOutline,
  IoTrendingDownOutline,
  IoTrendingUpOutline,
  IoReceiptOutline,
} from 'react-icons/io5';
import { api, type FinanceTransaction } from '../api';

const RUB = (v: number) => v.toLocaleString('ru-RU', { maximumFractionDigits: 2 }) + ' ₽';

const PERIOD_PRESETS = [
  { id: 'today', label: 'Сегодня', days: 0 },
  { id: 'week', label: '7 дней', days: 7 },
  { id: '14days', label: '14 дней', days: 14 },
  { id: 'month', label: '30 дней', days: 30 },
] as const;

const TYPE_LABELS: Record<string, string> = {
  orders: 'Продажи',
  returns: 'Возвраты',
  services: 'Услуги',
  compensation: 'Компенсации',
  transferDelivery: 'Логистика',
  other: 'Прочее',
};

export function FinancePage() {
  const [presetId, setPresetId] = useState<(typeof PERIOD_PRESETS)[number]['id']>('week');
  const preset = PERIOD_PRESETS.find((p) => p.id === presetId)!;

  // useMemo — иначе Date-объекты пересоздаются каждый рендер,
  // queryKey меняется и React Query крутится в бесконечном re-fetch.
  const { from, to, fromIso, toIso } = useMemo(() => {
    const { from, to } = computePeriod(preset.days);
    return { from, to, fromIso: from.toISOString(), toIso: to.toISOString() };
  }, [preset.days]);

  const summary = useQuery({
    queryKey: ['finance-summary', presetId],
    queryFn: () => api.financeSummary(fromIso, toIso),
  });
  const byDay = useQuery({
    queryKey: ['finance-by-day', presetId],
    queryFn: () => api.financeByDay(fromIso, toIso),
  });
  const top = useQuery({
    queryKey: ['finance-top-products', presetId],
    queryFn: () => api.financeTopProducts(fromIso, toIso, 20),
  });

  const [txType, setTxType] = useState<string>('');
  const tx = useQuery({
    queryKey: ['finance-tx', presetId, txType],
    queryFn: () => api.financeTransactions({
      from: fromIso,
      to: toIso,
      type: txType || undefined,
      limit: 100,
    }),
  });

  const s = summary.data;
  const profit = s ? s.grossRevenue + s.totalCommissions : 0; // commission уже отрицательная

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Финансы</h2>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Транзакции из Ozon · автосинк каждый час
          </div>
        </div>
        <div className="status-tabs">
          {PERIOD_PRESETS.map((p) => (
            <button
              key={p.id}
              className={`status-tab${presetId === p.id ? ' active' : ''}`}
              onClick={() => setPresetId(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {summary.isLoading && <div className="card">Загрузка…</div>}

      {s && (
        <>
          {/* Основная сводка */}
          <div className="dashboard-kpi-grid" style={{ marginBottom: 20 }}>
            <KpiCard icon={<IoCashOutline />} label="Выручка от продаж" value={RUB(s.grossRevenue)} accent />
            <KpiCard icon={<IoTrendingDownOutline />} label="Комиссия Ozon" value={RUB(-s.totalCommissions)} sub={`${((-s.totalCommissions / Math.max(1, s.grossRevenue)) * 100).toFixed(1)}% от выручки`} negative />
            <KpiCard icon={<IoTrendingUpOutline />} label="Прибыль после комиссий" value={RUB(profit)} sub="без вычета себестоимости" />
            <KpiCard icon={<IoReceiptOutline />} label="Сумма к выплате" value={RUB(s.netPayout)} sub={`${s.operations} операций`} />
          </div>

          {/* Разбивка по типам */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ padding: '20px 20px 0' }}>
              <h3 style={{ margin: 0 }}>Разбивка по типам операций</h3>
            </div>
            <table className="products" style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Тип</th>
                  <th className="num">Операций</th>
                  <th className="num">Выручка</th>
                  <th className="num">Комиссия</th>
                  <th className="num">Итого</th>
                </tr>
              </thead>
              <tbody>
                {s.byType.map((r) => (
                  <tr key={r.type}>
                    <td>{TYPE_LABELS[r.type] ?? r.type}</td>
                    <td className="num">{r.count}</td>
                    <td className="num">{r.accruals > 0 ? RUB(r.accruals) : '—'}</td>
                    <td className="num" style={{ color: r.commission < 0 ? 'var(--red)' : undefined }}>{r.commission !== 0 ? RUB(r.commission) : '—'}</td>
                    <td className="num" style={{ color: r.amount < 0 ? 'var(--red)' : r.amount > 0 ? 'var(--green)' : undefined, fontWeight: 600 }}>
                      {RUB(r.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* График по дням */}
          {byDay.data && byDay.data.items.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ padding: '20px 20px 12px' }}>
                <h3 style={{ margin: 0 }}>Выручка по дням</h3>
              </div>
              <DailyBarChart data={byDay.data.items} />
            </div>
          )}

          {/* Топ товаров */}
          {top.data && top.data.items.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ padding: '20px 20px 0' }}>
                <h3 style={{ margin: 0 }}>Топ товаров по выручке</h3>
              </div>
              <table className="products" style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Товар</th>
                    <th>SKU</th>
                    <th className="num">Заказов</th>
                    <th className="num">Выручка</th>
                    <th className="num">Комиссия</th>
                  </tr>
                </thead>
                <tbody>
                  {top.data.items.map((p, i) => (
                    <tr key={p.sku}>
                      <td className="muted">{i + 1}</td>
                      <td style={{ fontSize: 13 }}>{p.name}</td>
                      <td><code style={{ fontSize: 11 }}>{p.sku}</code></td>
                      <td className="num">{p.orders_count}</td>
                      <td className="num"><strong>{RUB(p.revenue)}</strong></td>
                      <td className="num" style={{ color: 'var(--red)' }}>{RUB(-p.commission)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Транзакции */}
          <div className="card">
            <div style={{ padding: '20px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <h3 style={{ margin: 0 }}>Транзакции</h3>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className={`status-tab${txType === '' ? ' active' : ''}`} onClick={() => setTxType('')}>Все</button>
                {['orders', 'returns', 'services', 'other'].map((t) => (
                  <button key={t} className={`status-tab${txType === t ? ' active' : ''}`} onClick={() => setTxType(t)}>
                    {TYPE_LABELS[t] ?? t}
                  </button>
                ))}
              </div>
            </div>
            <table className="products" style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Тип / описание</th>
                  <th>Отправление</th>
                  <th>Товар</th>
                  <th className="num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {tx.data?.items.length === 0 && <tr><td colSpan={5} className="empty">Нет транзакций за период.</td></tr>}
                {tx.data?.items.map((t) => <TxRow key={t.operationId} t={t} />)}
              </tbody>
            </table>
            {tx.data && tx.data.total > tx.data.items.length && (
              <div className="muted" style={{ padding: 12, fontSize: 12, textAlign: 'center' }}>
                Показано {tx.data.items.length} из {tx.data.total}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}

function KpiCard({ icon, label, value, sub, accent, negative }: { icon: React.ReactNode; label: string; value: string; sub?: string; accent?: boolean; negative?: boolean }) {
  return (
    <div className="kpi-card">
      <div className="kpi-icon">{icon}</div>
      <div className="kpi-label">{label}</div>
      <div className={`kpi-value${accent ? ' accent' : ''}${negative ? ' negative' : ''}`}>{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

function TxRow({ t }: { t: FinanceTransaction }) {
  const amount = Number(t.amount);
  const productName = t.items?.[0]?.name;
  return (
    <tr>
      <td className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        {new Date(t.operationDate).toLocaleDateString('ru-RU')}
        <br />
        <span style={{ fontSize: 10 }}>{new Date(t.operationDate).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
      </td>
      <td style={{ fontSize: 12 }}>
        <div style={{ fontWeight: 500 }}>{TYPE_LABELS[t.type ?? ''] ?? t.type}</div>
        <div className="muted" style={{ fontSize: 11 }}>{t.operationTypeName}</div>
      </td>
      <td><code style={{ fontSize: 11 }}>{t.postingNumber ?? '—'}</code></td>
      <td style={{ fontSize: 12, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{productName ?? '—'}</td>
      <td className="num" style={{ color: amount < 0 ? 'var(--red)' : amount > 0 ? 'var(--green)' : undefined, fontWeight: 600 }}>
        {RUB(amount)}
      </td>
    </tr>
  );
}

function DailyBarChart({ data }: { data: { day: string; revenue: number; commission: number; payout: number; orders: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.revenue));
  return (
    <div style={{ padding: '0 20px 20px', display: 'flex', gap: 4, alignItems: 'flex-end', height: 180, overflowX: 'auto' }}>
      {data.map((d, i) => {
        const h = (d.revenue / max) * 140;
        return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 28 }}>
            <div className="muted" style={{ fontSize: 9, fontVariantNumeric: 'tabular-nums' }}>{d.revenue > 0 ? Math.round(d.revenue / 1000) + 'к' : ''}</div>
            <div
              style={{
                width: 22,
                height: Math.max(2, h),
                background: 'linear-gradient(180deg, #005bff 0%, #0089ff 100%)',
                borderRadius: '4px 4px 0 0',
              }}
              title={`${new Date(d.day).toLocaleDateString('ru-RU')}: ${RUB(d.revenue)} (${d.orders} заказов)`}
            />
            <div className="muted" style={{ fontSize: 10, transform: 'rotate(-45deg)', transformOrigin: 'top left', whiteSpace: 'nowrap', marginTop: 4 }}>
              {new Date(d.day).getDate()}.{new Date(d.day).getMonth() + 1}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function computePeriod(days: number): { from: Date; to: Date } {
  const to = new Date();
  const from = days === 0
    ? new Date(to.getFullYear(), to.getMonth(), to.getDate())
    : new Date(Date.now() - days * 24 * 3600_000);
  return { from, to };
}
